/**
 * Client service for Sector Operations Shared Devices.
 * R39 SO-P75 (A17.a1).
 *
 * Wraps Cloud Function callables for device enrolment, PIN management,
 * user switch-in/switch-out, queued offline submissions, and local
 * credential persistence.
 */

import { useEffect, useState } from 'react'
import { signInWithCustomToken, type UserCredential } from 'firebase/auth'
import { firebaseAuth } from './firebase'
import { callable } from './callable'
import * as authContextModule from '../context/useAuth'
import type {
  CreateDeviceEnrolCodeInput,
  CreateDeviceEnrolCodeResult,
  EnrolSharedDeviceInput,
  EnrolSharedDeviceResult,
  RevokeSharedDeviceInput,
  RevokeSharedDeviceResult,
  SetSharedDeviceSettingsInput,
  SetSharedDeviceSettingsResult,
  ListSharedDevicesInput,
  ListSharedDevicesResult,
  ListSharedDeviceSessionsInput,
  ListSharedDeviceSessionsResult,
  SetMySharedDevicePinInput,
  SetMySharedDevicePinResult,
  SetSharedDevicePinByPhoneInput,
  SetSharedDevicePinByPhoneResult,
  ResetSharedDevicePinInput,
  ResetSharedDevicePinResult,
  ListSharedDevicePeopleInput,
  ListSharedDevicePeopleResult,
  SharedDeviceSwitchInInput,
  SharedDeviceSwitchInResult,
  SharedDeviceSwitchOutInput,
  SharedDeviceSwitchOutResult,
  SubmitSharedDeviceQueuedInput,
  SubmitSharedDeviceQueuedResult,
  IssueSharedDeviceUploadUrlInput,
  IssueSharedDeviceUploadUrlResult,
  FinalizeSharedDeviceUploadInput,
  FinalizeSharedDeviceUploadResult,
} from '../types/ops'

// ---------------------------------------------------------------------------
// Credential Storage
// ---------------------------------------------------------------------------

export const SHARED_DEVICE_STORAGE_KEY = 'ops.sharedDevice.credentials'

export interface StoredSharedDeviceCredentials {
  deviceId: string
  deviceSecret: string
  workspaceId: string
  placeId: string
  label: string
  idleLockMinutes: number
}

function getStorage(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage
    }
    const g = globalThis as unknown as { localStorage?: Storage }
    if (typeof globalThis !== 'undefined' && g.localStorage) {
      return g.localStorage
    }
  } catch {
    return null
  }
  return null
}

export function getStoredSharedDeviceCredentials(): StoredSharedDeviceCredentials | null {
  try {
    const storage = getStorage()
    if (!storage) return null
    const raw = storage.getItem(SHARED_DEVICE_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (
      parsed &&
      typeof parsed.deviceId === 'string' &&
      typeof parsed.deviceSecret === 'string' &&
      typeof parsed.workspaceId === 'string' &&
      typeof parsed.placeId === 'string' &&
      typeof parsed.label === 'string' &&
      typeof parsed.idleLockMinutes === 'number'
    ) {
      return parsed as StoredSharedDeviceCredentials
    }
    return null
  } catch {
    return null
  }
}

export function setStoredSharedDeviceCredentials(
  creds: StoredSharedDeviceCredentials,
): void {
  try {
    const storage = getStorage()
    if (!storage) return
    storage.setItem(SHARED_DEVICE_STORAGE_KEY, JSON.stringify(creds))
  } catch {
    // Swallow quota or private-browsing errors
  }
}

export function clearStoredSharedDeviceCredentials(): void {
  try {
    const storage = getStorage()
    if (!storage) return
    storage.removeItem(SHARED_DEVICE_STORAGE_KEY)
  } catch {
    // Swallow errors
  }
}

// ---------------------------------------------------------------------------
// Auth Session Sign-In
// ---------------------------------------------------------------------------

export async function signInSharedDeviceSession(
  customToken: string,
): Promise<UserCredential> {
  return signInWithCustomToken(firebaseAuth, customToken)
}

export async function signOutSharedDeviceSession(): Promise<void> {
  return firebaseAuth.signOut()
}

/**
 * Hook to read the `sharedDevice` custom claim on the current auth token.
 * Returns false when unauthenticated or when the claim is not true.
 */
interface TokenBearingUser {
  uid?: string
  getIdTokenResult?: (force?: boolean) => Promise<{ claims?: Record<string, unknown> }>
}

export function useSharedDeviceClaim(): boolean {
  let auth: unknown = null
  try {
    if (typeof authContextModule.useOptionalAuth === 'function') {
      auth = authContextModule.useOptionalAuth()
    } else if (typeof (authContextModule as { useAuth?: () => unknown }).useAuth === 'function') {
      auth = (authContextModule as { useAuth: () => unknown }).useAuth()
    }
  } catch {
    auth = null
  }
  const user = ((auth as { currentUser?: TokenBearingUser })?.currentUser ||
    (auth as { user?: TokenBearingUser })?.user ||
    (firebaseAuth?.currentUser as TokenBearingUser | null) ||
    null) as TokenBearingUser | null
  const currentUid = user?.uid
  const [claimUid, setClaimUid] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!user || typeof user.getIdTokenResult !== 'function') {
      return
    }

    user
      .getIdTokenResult(false)
      .then((tokenResult) => {
        if (cancelled) return
        if (tokenResult?.claims?.sharedDevice === true) {
          setClaimUid(user.uid ?? null)
        } else {
          setClaimUid(null)
        }
      })
      .catch(() => {
        if (cancelled) return
        setClaimUid(null)
      })

    return () => {
      cancelled = true
    }
  }, [user, currentUid])

  return Boolean(currentUid && claimUid === currentUid)
}

// ---------------------------------------------------------------------------
// Callables
// ---------------------------------------------------------------------------

export async function createDeviceEnrolCode(
  input: CreateDeviceEnrolCodeInput,
): Promise<CreateDeviceEnrolCodeResult> {
  return callable<CreateDeviceEnrolCodeInput, CreateDeviceEnrolCodeResult>(
    'createDeviceEnrolCode',
    input,
  )
}

export async function enrolSharedDevice(
  input: EnrolSharedDeviceInput,
): Promise<EnrolSharedDeviceResult> {
  return callable<EnrolSharedDeviceInput, EnrolSharedDeviceResult>(
    'enrolSharedDevice',
    input,
  )
}

export async function revokeSharedDevice(
  input: RevokeSharedDeviceInput,
): Promise<RevokeSharedDeviceResult> {
  return callable<RevokeSharedDeviceInput, RevokeSharedDeviceResult>(
    'revokeSharedDevice',
    input,
  )
}

export async function setSharedDeviceSettings(
  input: SetSharedDeviceSettingsInput,
): Promise<SetSharedDeviceSettingsResult> {
  return callable<SetSharedDeviceSettingsInput, SetSharedDeviceSettingsResult>(
    'setSharedDeviceSettings',
    input,
  )
}

export async function listSharedDevices(
  input: ListSharedDevicesInput,
): Promise<ListSharedDevicesResult> {
  return callable<ListSharedDevicesInput, ListSharedDevicesResult>(
    'listSharedDevices',
    input,
  )
}

export async function listSharedDeviceSessions(
  input: ListSharedDeviceSessionsInput,
): Promise<ListSharedDeviceSessionsResult> {
  return callable<ListSharedDeviceSessionsInput, ListSharedDeviceSessionsResult>(
    'listSharedDeviceSessions',
    input,
  )
}

export async function setMySharedDevicePin(
  input: SetMySharedDevicePinInput,
): Promise<SetMySharedDevicePinResult> {
  return callable<SetMySharedDevicePinInput, SetMySharedDevicePinResult>(
    'setMySharedDevicePin',
    input,
  )
}

export async function setSharedDevicePinByPhone(
  input: SetSharedDevicePinByPhoneInput,
): Promise<SetSharedDevicePinByPhoneResult> {
  return callable<SetSharedDevicePinByPhoneInput, SetSharedDevicePinByPhoneResult>(
    'setSharedDevicePinByPhone',
    input,
  )
}

export async function resetSharedDevicePin(
  input: ResetSharedDevicePinInput,
): Promise<ResetSharedDevicePinResult> {
  return callable<ResetSharedDevicePinInput, ResetSharedDevicePinResult>(
    'resetSharedDevicePin',
    input,
  )
}

export async function listSharedDevicePeople(
  input: ListSharedDevicePeopleInput,
): Promise<ListSharedDevicePeopleResult> {
  return callable<ListSharedDevicePeopleInput, ListSharedDevicePeopleResult>(
    'listSharedDevicePeople',
    input,
  )
}

export async function sharedDeviceSwitchIn(
  input: SharedDeviceSwitchInInput,
): Promise<SharedDeviceSwitchInResult> {
  return callable<SharedDeviceSwitchInInput, SharedDeviceSwitchInResult>(
    'sharedDeviceSwitchIn',
    input,
  )
}

export async function sharedDeviceSwitchOut(
  input: SharedDeviceSwitchOutInput,
): Promise<SharedDeviceSwitchOutResult> {
  return callable<SharedDeviceSwitchOutInput, SharedDeviceSwitchOutResult>(
    'sharedDeviceSwitchOut',
    input,
  )
}

export async function submitSharedDeviceQueued(
  input: SubmitSharedDeviceQueuedInput,
): Promise<SubmitSharedDeviceQueuedResult> {
  return callable<SubmitSharedDeviceQueuedInput, SubmitSharedDeviceQueuedResult>(
    'submitSharedDeviceQueued',
    input,
  )
}

export async function issueSharedDeviceUploadUrl(
  input: IssueSharedDeviceUploadUrlInput,
): Promise<IssueSharedDeviceUploadUrlResult> {
  return callable<IssueSharedDeviceUploadUrlInput, IssueSharedDeviceUploadUrlResult>(
    'issueSharedDeviceUploadUrl',
    input,
  )
}

export async function finalizeSharedDeviceUpload(
  input: FinalizeSharedDeviceUploadInput,
): Promise<FinalizeSharedDeviceUploadResult> {
  return callable<FinalizeSharedDeviceUploadInput, FinalizeSharedDeviceUploadResult>(
    'finalizeSharedDeviceUpload',
    input,
  )
}
